// Phase 24b · Admin Upsell dashboard.
// Route: /admin/upsell (admin only). Tabs: Overview / Events / Rules / Preview.
import React, { useEffect, useState, useCallback } from "react";
import {
    BarChart3, ListChecks, ToggleLeft, ToggleRight, Beaker,
    Download, RefreshCw, CheckCircle, XCircle, Zap,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const SURFACES = [
    "ai_feed", "ai_groups", "ai_events", "ai_rooms", "ai_creator",
    "community_feed", "community_groups", "community_events", "community_rooms",
    "marketplace",
];

const TABS = [
    { id: "overview", label: "Overview", Icon: BarChart3 },
    { id: "events", label: "Events log", Icon: ListChecks },
    { id: "rules", label: "Rules", Icon: ToggleLeft },
    { id: "preview", label: "Preview", Icon: Beaker },
];

function fmt(dt) {
    if (!dt) return "";
    try { return new Date(dt).toLocaleString(); } catch { return dt; }
}

export default function AdminUpsell() {
    const [tab, setTab] = useState("overview");
    const [overview, setOverview] = useState(null);
    const [events, setEvents] = useState([]);
    const [rules, setRules] = useState([]);
    const [loading, setLoading] = useState(false);
    const [previewUser, setPreviewUser] = useState("");
    const [previewSurface, setPreviewSurface] = useState("ai_feed");
    const [previewResult, setPreviewResult] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            if (tab === "overview") {
                const r = await fetch(`${API}/api/admin/upsell/overview`, { credentials: "include" });
                if (r.ok) setOverview(await r.json());
            } else if (tab === "events") {
                const r = await fetch(`${API}/api/admin/upsell/events?limit=100`, { credentials: "include" });
                if (r.ok) setEvents(((await r.json()) || {}).items || []);
            } else if (tab === "rules") {
                const r = await fetch(`${API}/api/admin/upsell/rules`, { credentials: "include" });
                if (r.ok) setRules(((await r.json()) || {}).rules || []);
            }
        } catch {}
        setLoading(false);
    }, [tab]);

    useEffect(() => { document.title = "Admin · Upsell · Global Lead Suite"; load(); }, [load]);

    const toggleRule = async (rule, enabled) => {
        const r = await fetch(`${API}/api/admin/upsell/rules/toggle`, {
            method: "POST", credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ rule, enabled }),
        });
        if (r.ok) load();
    };

    const runPreview = async () => {
        if (!previewUser || !previewSurface) return;
        const r = await fetch(
            `${API}/api/admin/upsell/preview?user_id=${encodeURIComponent(previewUser)}&surface=${encodeURIComponent(previewSurface)}`,
            { credentials: "include" },
        );
        if (r.ok) setPreviewResult(await r.json());
    };

    return (
        <div className="min-h-screen bg-[#0A0A0A] text-neutral-100">
            <div className="max-w-7xl mx-auto px-4 py-6">
                <div className="flex items-start justify-between mb-6">
                    <div>
                        <h1 className="text-2xl font-bold" data-testid="admin-upsell-title">Upsell AI Engine V2</h1>
                        <p className="text-xs text-neutral-500 mt-0.5">
                            Phase 24b · Rules-based CTA + LLM ($2/day + 500 gen/day + 6h cache)
                        </p>
                    </div>
                    <div className="flex gap-2">
                        <button
                            onClick={load}
                            data-testid="admin-upsell-refresh"
                            className="text-xs px-3 py-1.5 rounded-full border border-[#1A1A1A] hover:border-[#CCCCCC]/50 inline-flex items-center gap-1"
                        >
                            <RefreshCw size={12} /> Refresh
                        </button>
                        <a
                            href={`${API}/api/admin/upsell/events/export.csv`}
                            data-testid="admin-upsell-csv-export"
                            className="text-xs px-3 py-1.5 rounded-full border border-[#CCCCCC]/40 hover:bg-[#CCCCCC]/10 inline-flex items-center gap-1.5"
                        >
                            <Download size={12} /> Events CSV
                        </a>
                    </div>
                </div>

                <div className="flex flex-wrap gap-1 mb-6 border-b border-[#1A1A1A]" data-testid="admin-upsell-tabs">
                    {TABS.map(({ id, label, Icon }) => (
                        <button
                            key={id}
                            onClick={() => setTab(id)}
                            data-testid={`admin-upsell-tab-${id}`}
                            className={`text-xs px-3 py-2 inline-flex items-center gap-1.5 transition ${
                                tab === id
                                    ? "text-[#CCCCCC] border-b-2 border-[#CCCCCC]"
                                    : "text-neutral-500 hover:text-neutral-300"
                            }`}
                        >
                            <Icon size={12} /> {label}
                        </button>
                    ))}
                </div>

                {loading && <div className="text-sm text-neutral-500 mb-3">Loading…</div>}

                {tab === "overview" && overview && (
                    <div className="space-y-4" data-testid="admin-upsell-overview">
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                            <div className="rounded-xl border border-[#1A1A1A] bg-[#111111] p-4">
                                <p className="text-xs text-neutral-500">Impressions today</p>
                                <p className="text-2xl font-bold" data-testid="kpi-impressions">{overview.impressions_today}</p>
                            </div>
                            <div className="rounded-xl border border-[#1A1A1A] bg-[#111111] p-4">
                                <p className="text-xs text-neutral-500">Clicks today</p>
                                <p className="text-2xl font-bold text-[#CCCCCC]" data-testid="kpi-clicks">{overview.clicks_today}</p>
                            </div>
                            <div className="rounded-xl border border-[#1A1A1A] bg-[#111111] p-4">
                                <p className="text-xs text-neutral-500">CTR</p>
                                <p className="text-2xl font-bold text-neutral-100" data-testid="kpi-ctr">{(overview.ctr * 100).toFixed(1)}%</p>
                                <p className="text-[10px] text-neutral-500">CVR: {(overview.cvr * 100).toFixed(1)}%</p>
                            </div>
                            <div className="rounded-xl border border-[#1A1A1A] bg-[#111111] p-4">
                                <p className="text-xs text-neutral-500">Conversions today</p>
                                <p className="text-2xl font-bold text-[#CCCCCC]" data-testid="kpi-conversions">{overview.conversions_today}</p>
                                <p className="text-[10px] text-neutral-500">→ /pricing</p>
                            </div>
                        </div>
                        <div className="rounded-xl border border-[#1A1A1A] bg-[#111111] p-4">
                            <p className="text-xs text-neutral-500 uppercase tracking-widest mb-2">LLM budget today</p>
                            <div className="flex items-center gap-3">
                                <div className="flex-1 h-2 bg-[#0A0A0A] rounded-full overflow-hidden">
                                    <div
                                        className="h-full bg-gradient-to-r from-neutral-100 to-neutral-400"
                                        style={{ width: `${Math.min(100, (overview.budget_used_usd / overview.budget_cap_usd) * 100)}%` }}
                                    />
                                </div>
                                <span className="text-xs text-neutral-300 font-mono" data-testid="kpi-budget">
                                    ${overview.budget_used_usd.toFixed(4)} / ${overview.budget_cap_usd.toFixed(2)}
                                </span>
                            </div>
                            <p className="text-[10px] text-neutral-500 mt-2">
                                CTA generations: {overview.cta_generations_today} / {overview.cta_generations_cap}
                                &nbsp;·&nbsp; Frequency caps: {overview.frequency_caps.per_surface_per_user_per_day}/surface/user/day, {overview.frequency_caps.per_user_per_day}/user/day
                            </p>
                        </div>
                        <div className="rounded-xl border border-[#1A1A1A] bg-[#111111] p-4">
                            <p className="text-xs text-neutral-500 uppercase tracking-widest mb-2">Top CTAs today</p>
                            {overview.top_ctas.length === 0 ? (
                                <p className="text-sm text-neutral-500">No impressions yet.</p>
                            ) : (
                                <div className="space-y-1">
                                    {overview.top_ctas.map((c) => (
                                        <div key={c.cta_slug} className="flex items-center gap-2 text-xs">
                                            <span className="font-mono text-neutral-300 flex-1">{c.cta_slug}</span>
                                            <span className="text-neutral-500">{c.impressions} impressions</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {tab === "events" && (
                    <div className="rounded-xl border border-[#1A1A1A] bg-[#111111] overflow-x-auto" data-testid="admin-upsell-events-table">
                        <table className="w-full text-xs">
                            <thead className="text-left text-neutral-500 border-b border-[#1A1A1A]">
                                <tr>
                                    <th className="px-3 py-2">timestamp</th>
                                    <th className="px-3 py-2">user_id</th>
                                    <th className="px-3 py-2">surface</th>
                                    <th className="px-3 py-2">cta_slug</th>
                                    <th className="px-3 py-2">variant</th>
                                    <th className="px-3 py-2">event_type</th>
                                </tr>
                            </thead>
                            <tbody>
                                {events.map((e) => (
                                    <tr key={e.id} className="border-b border-[#1A1A1A]" data-testid={`event-row-${e.id}`}>
                                        <td className="px-3 py-2 text-neutral-500">{fmt(e.timestamp)}</td>
                                        <td className="px-3 py-2 font-mono text-[10px] text-neutral-500 truncate max-w-[140px]">{e.user_id}</td>
                                        <td className="px-3 py-2">{e.surface}</td>
                                        <td className="px-3 py-2 font-mono text-[10px] text-neutral-300">{e.cta_slug}</td>
                                        <td className="px-3 py-2">{e.cta_variant}</td>
                                        <td className="px-3 py-2">
                                            <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                                                e.event_type === "impression" ? "bg-neutral-500/15 text-neutral-300" :
                                                e.event_type === "click" ? "bg-[#CCCCCC]/15 text-[#CCCCCC]" :
                                                e.event_type === "conversion" ? "bg-[#CCCCCC]/15 text-[#CCCCCC]" :
                                                "bg-[#FAFAFA]/15 text-[#FAFAFA]"
                                            }`}>{e.event_type}</span>
                                        </td>
                                    </tr>
                                ))}
                                {events.length === 0 && (
                                    <tr><td colSpan="6" className="px-3 py-6 text-center text-neutral-500">No events</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}

                {tab === "rules" && (
                    <div className="space-y-2" data-testid="admin-upsell-rules-list">
                        {rules.map((r) => (
                            <div
                                key={r.rule}
                                data-testid={`rule-row-${r.rule}`}
                                className="rounded-xl border border-[#1A1A1A] bg-[#111111] p-4 flex items-center gap-3"
                            >
                                <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold ${
                                    r.kind === "ai" ? "bg-[#161616]-500/15 text-[#CCCCCC]-300" : "bg-[#CCCCCC]/15 text-[#CCCCCC]"
                                }`}>
                                    {r.kind}
                                </span>
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm font-semibold">{r.rule}</p>
                                    <p className="text-[11px] text-neutral-500 truncate">{r.static_cta_text}</p>
                                </div>
                                <button
                                    onClick={() => toggleRule(r.rule, !r.enabled)}
                                    data-testid={`rule-toggle-${r.rule}`}
                                    className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full ${
                                        r.enabled ? "bg-[#CCCCCC]/15 text-[#CCCCCC]" : "bg-neutral-500/15 text-neutral-500"
                                    }`}
                                >
                                    {r.enabled ? <ToggleRight size={14} /> : <ToggleLeft size={14} />}
                                    {r.enabled ? "Enabled" : "Disabled"}
                                </button>
                            </div>
                        ))}
                    </div>
                )}

                {tab === "preview" && (
                    <div className="space-y-4" data-testid="admin-upsell-preview">
                        <div className="rounded-xl border border-[#1A1A1A] bg-[#111111] p-4">
                            <p className="text-xs text-neutral-500 uppercase tracking-widest mb-3">Simulate an upsell decision (no impression logged)</p>
                            <div className="flex flex-wrap items-end gap-2">
                                <div>
                                    <label className="text-[11px] text-neutral-500 block mb-1">user_id</label>
                                    <input
                                        value={previewUser}
                                        onChange={(e) => setPreviewUser(e.target.value)}
                                        data-testid="admin-upsell-preview-user"
                                        placeholder="e.g. user-uuid-1234"
                                        className="text-xs bg-[#0A0A0A] border border-[#1A1A1A] rounded-md px-2 py-1.5 w-64 font-mono"
                                    />
                                </div>
                                <div>
                                    <label className="text-[11px] text-neutral-500 block mb-1">surface</label>
                                    <select
                                        value={previewSurface}
                                        onChange={(e) => setPreviewSurface(e.target.value)}
                                        data-testid="admin-upsell-preview-surface"
                                        className="text-xs bg-[#0A0A0A] border border-[#1A1A1A] rounded-md px-2 py-1.5"
                                    >
                                        {SURFACES.map((s) => <option key={s} value={s}>{s}</option>)}
                                    </select>
                                </div>
                                <button
                                    onClick={runPreview}
                                    data-testid="admin-upsell-preview-run"
                                    className="text-xs px-4 py-1.5 rounded-full bg-neutral-100 text-neutral-900 hover:bg-neutral-200 inline-flex items-center gap-1"
                                >
                                    <Zap size={12} /> Run
                                </button>
                            </div>
                        </div>
                        {previewResult && (
                            <div className="rounded-xl border border-[#1A1A1A] bg-[#111111] p-4" data-testid="admin-upsell-preview-result">
                                <p className="text-xs text-neutral-500 mb-2">Result:</p>
                                <pre className="text-[11px] text-neutral-300 whitespace-pre-wrap font-mono">
                                    {JSON.stringify(previewResult, null, 2)}
                                </pre>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
